import { useEffect, useRef } from "react";
import { usePrefersReducedMotion } from "../../hooks/useBreakpoint";

interface ConfettiProps {
  /** Bump this to fire a fresh burst. */
  fireKey: number | string | null;
  /** 0-3 — scales particle count, spread and streamer size. */
  intensity?: 0 | 1 | 2 | 3;
  /** Extra colours mixed into the brand palette (e.g. the tier colour). */
  colors?: string[];
  className?: string;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  w: number;
  h: number;
  color: string;
  shape: "rect" | "circle" | "strip";
  life: number;
  maxLife: number;
}

const BRAND = ["#dd54eb", "#5460ed", "#8a5cf0", "#ffd6fb", "#ffffff", "#f59e0b"];

/**
 * Canvas confetti burst. Purely decorative — renders nothing for users who
 * prefer reduced motion.
 */
export function Confetti({ fireKey, intensity = 2, colors, className = "" }: ConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const timeoutsRef = useRef<number[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const reduced = usePrefersReducedMotion();

  // Read through refs so a fresh `colors` array literal from the caller can't
  // retrigger the burst on every render — only `fireKey` fires it.
  const intensityRef = useRef(intensity);
  const colorsRef = useRef(colors);
  intensityRef.current = intensity;
  colorsRef.current = colors;

  useEffect(() => {
    if (fireKey == null || reduced) return;
    const intensityNow = intensityRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const { clientWidth: w, clientHeight: h } = canvas;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const palette = [...BRAND, ...(colorsRef.current ?? [])];
    const count = [40, 80, 140, 220][intensityNow] ?? 140;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    const spawn = (originX: number, originY: number, power: number) => {
      for (let i = 0; i < count / 2; i += 1) {
        const angle = Math.random() * Math.PI * 2;
        const speed = (2 + Math.random() * 7) * power;
        const shapeRoll = Math.random();
        particlesRef.current.push({
          x: originX,
          y: originY,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 3,
          rot: Math.random() * Math.PI,
          vr: (Math.random() - 0.5) * 0.34,
          w: 5 + Math.random() * 7,
          h: 8 + Math.random() * 12,
          color: palette[Math.floor(Math.random() * palette.length)]!,
          shape: shapeRoll > 0.72 ? "strip" : shapeRoll > 0.4 ? "circle" : "rect",
          life: 0,
          maxLife: 90 + Math.random() * 70,
        });
      }
    };

    particlesRef.current = [];
    spawn(w / 2, h * 0.42, 1);
    if (intensityNow >= 2) {
      timeoutsRef.current.push(
        window.setTimeout(() => spawn(w * 0.2, h * 0.5, 0.85), 140),
        window.setTimeout(() => spawn(w * 0.8, h * 0.5, 0.85), 240),
      );
    }

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      const alive: Particle[] = [];
      for (const p of particlesRef.current) {
        p.life += 1;
        p.vy += 0.16;
        p.vx *= 0.992;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        if (p.life > p.maxLife || p.y > h + 60) continue;
        alive.push(p);

        const fade = Math.max(0, 1 - p.life / p.maxLife);
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === "circle") {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === "strip") {
          ctx.fillRect(-p.w / 4, -p.h, p.w / 2, p.h * 2);
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      }
      particlesRef.current = alive;
      if (alive.length) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, w, h);
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      timeoutsRef.current.forEach(window.clearTimeout);
      timeoutsRef.current = [];
      particlesRef.current = [];
    };
  }, [fireKey, reduced]);

  if (reduced) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    />
  );
}
